import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Semantisch korrekte, einheitliche Gewerk-Icons als Inline-SVG (Lucide-Stil,
 * 24×24, stroke). Ersetzt die PrimeNG-`pi-*`-Klassen vom Backend, die für
 * mehrere Gewerke keine passenden Symbole hatten (Gartenbau→Kompass,
 * Heizung→Sonne, Reinigung→Funkeln). Schlüssel ist die PascalCase-Gewerk-Id
 * (`nameof(Gewerk.X)` aus dem Backend), nicht das `icon`-Feld der DTO.
 */
@Component({
  selector: 'app-gewerk-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      [attr.width]="size()"
      [attr.height]="size()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      @switch (gewerkId()) {
        @case ('Elektriker') {
          <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
        }
        @case ('Sanitaer') {
          <!-- Rohr mit Tropfen -->
          <path d="M12 22a5 5 0 0 0 5-5c0-3-5-9-5-9s-5 6-5 9a5 5 0 0 0 5 5z" />
        }
        @case ('Maler') {
          <!-- Farbroller -->
          <rect x="3" y="4" width="13" height="6" rx="1.5" />
          <path d="M16 7h3a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-6a1 1 0 0 0-1 1v2" />
          <rect x="10" y="16" width="4" height="6" rx="1" />
        }
        @case ('Tischler') {
          <!-- Hammer -->
          <path d="m15 12-8.5 8.5a2.12 2.12 0 1 1-3-3L12 9" />
          <path d="M17.6 15 22 10.6" />
          <path d="m20.9 11.7-1.2-1.3c-.6-.6-.9-1.4-.9-2.2v-.9L16 4.6A5.6 5.6 0 0 0 12 3H9l.9.8A6.2 6.2 0 0 1 12 8.4V10l2 2h.9c.8 0 1.6.3 2.2.9l1.3 1.2" />
        }
        @case ('Bodenleger') {
          <!-- Fliesen-/Boden-Raster -->
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        }
        @case ('Gartenbau') {
          <!-- Spross / Pflanze -->
          <path d="M7 20h10" />
          <path d="M10 20c5.5-2.5.8-6.4 3-10" />
          <path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" />
          <path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z" />
        }
        @case ('Schluesseldienst') {
          <!-- Schlüssel -->
          <circle cx="7.5" cy="15.5" r="4.5" />
          <path d="m10.7 12.3 9.3-9.3" />
          <path d="m16 6 3 3" />
          <path d="m18 4 3 3" />
        }
        @case ('Heizung') {
          <!-- Flamme -->
          <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.1-2.1-.2-4 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.2.4-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
        }
        @case ('Reinigung') {
          <!-- Sprühflasche -->
          <path d="M3 3h.01M6 5h.01M3 7h.01M6 9h.01M3 11h.01" />
          <rect x="14" y="4" width="4" height="4" rx="1" />
          <path d="M11 12a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1z" />
        }
        @case ('Dachdecker') {
          <!-- Dach / Haus -->
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
        }
        @case ('TransportUmzug') {
          <!-- LKW -->
          <path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h1" />
          <path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1" />
          <circle cx="7.5" cy="18.5" r="1.5" />
          <circle cx="17.5" cy="18.5" r="1.5" />
          <path d="M9 18.5h6" />
        }
        @default {
          <!-- Fallback: Werkzeug -->
          <path d="M14.7 6.3a4 4 0 0 0 5 5l-1.8 4.6a2 2 0 0 1-3.4.6l-7-7a2 2 0 0 1 .6-3.4z" />
        }
      }
    </svg>
  `,
})
export class GewerkIconComponent {
  /** PascalCase-Gewerk-Id, z.B. "Elektriker", "TransportUmzug". */
  readonly gewerkId = input.required<string>();
  readonly size = input<number>(20);
}
